import React, { useState, useEffect } from 'react'
import { path } from 'ramda'
import Switch from 'react-switch'
import { Loader } from '../../components'
import { fetchFromBilling } from './billingAccess'
import { subIsIap, subIsIos, subIsPlus } from './index'
import { isAndroidApp, isApp, isIosApp } from '../../common/ambient'
import { usePaymentPolling } from './usePaymentPolling'
import bindDeviceActions from '../device/redux/bindDeviceActions'

// Auto-renew toggle component
function AutoRenewToggle ({ sub, user, device, userActions, actions }) {
  const [isUpdating, setIsUpdating] = useState(false)
  const [expectedCancelAtPeriodEnd, setExpectedCancelAtPeriodEnd] = useState(null)

  const { startPolling, stopPolling } = usePaymentPolling(actions, () => {
    setIsUpdating(false)
    setExpectedCancelAtPeriodEnd(null)
  })

  // Watch for subscription change and stop polling early
  useEffect(() => {
    if (!isUpdating || expectedCancelAtPeriodEnd === null) return

    if (sub.cancelAtPeriodEnd === expectedCancelAtPeriodEnd) {
      stopPolling()
      setIsUpdating(false)
      setExpectedCancelAtPeriodEnd(null)
    }
  }, [sub.cancelAtPeriodEnd, isUpdating, expectedCancelAtPeriodEnd, stopPolling])

  // Loading state
  if (isUpdating) {
    return <Loader />
  }

  // Free status
  if (sub.status === 'free') {
    return <div className='placeholder' />
  }

  // IAP subscriptions on wrong platform or web - show manage links
  if (subIsIap(sub) && (
    (!isApp()) || // Not in any app
    (subIsIos(sub) && !isIosApp()) || // Apple sub but not on iOS
    (!subIsIos(sub) && !isAndroidApp()) // Google sub but not on Android
  )) {
    if (subIsIos(sub)) {
      return <a href='https://apps.apple.com/account/subscriptions' target='_blank' rel='noreferrer'>Manage in App Store</a>
    } else {
      return <a href={`https://play.google.com/store/account/subscriptions?sku=${sub.googleProductId}&package=net.ambientweather.dashboard`} target='_blank' rel='noreferrer'>Manage in Play Store</a>
    }
  }

  // Gift code subscriptions without auto-renew change allowed
  if (subIsPlus(sub) && sub.promoCode && !sub.autoRenewChangeIsAllowed) {
    return 'Free'
  }

  // Station deleted
  const devices = path(['devices'], device) || []
  if (sub.macAddress && !devices.find(d => d.macAddress === sub.macAddress)) {
    return 'Station deleted'
  }

  // Default: show toggle switch
  return (
    <Switch
      onChange={async () => {
        if (subIsIap(sub)) {
          if (isIosApp()) {
            window.webkit.messageHandlers.showManageSubscriptions.postMessage({})
          } else if (window.ANDROID) {
            window.ANDROID.showManageSubscriptions(sub.googleProductId, path(['info', '_id'], user))
          }
        } else {
          setIsUpdating(true)
          try {
            const cancel_at_period_end = !sub.cancelAtPeriodEnd
            setExpectedCancelAtPeriodEnd(cancel_at_period_end)

            await fetchFromBilling(`/subscriptions?userId=${path(['info', '_id'], user)}&macAddress=${sub.macAddress}&productName=${sub.productName}`, {
              method: 'PUT',
              body: JSON.stringify({
                cancel_at_period_end
              }),
              headers: {
                'Content-Type': 'application/json'
              }
            })
            startPolling()
          } catch (error) {
            console.error('Error toggling auto-renew:', error)
            setIsUpdating(false)
            setExpectedCancelAtPeriodEnd(null)
            stopPolling()
          }
        }
      }}
      checked={!sub.cancelAtPeriodEnd}
    />
  )
}

AutoRenewToggle.displayName = 'AutoRenewToggle'

export default bindDeviceActions(AutoRenewToggle)
